import { useState, useRef } from "react";
import {
  Button,
  Icon,
  Textarea,
  useToast,
  FileItem,
} from "@elaniintc/goes-design-system";
import Modal from "@/components/common/modal";
import { useExemptPaymentRequest } from "@/queries/payment";

export interface ExemptPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  paymentRequestId: string;
  flowExecutionId?: string;
}

export const ExemptPaymentModal = ({
  isOpen,
  onClose,
  paymentRequestId,
  flowExecutionId,
}: ExemptPaymentModalProps) => {
  const [comments, setComments] = useState("");
  const [attachment, setAttachment] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();

  const { mutate: exemptPayment, isPending } =
    useExemptPaymentRequest(flowExecutionId);

  const handleSubmit = () => {
    if (!comments.trim()) {
      showToast({
        title: "Error",
        description: "Los comentarios son obligatorios",
        options: {
          type: "error",
          duration: 4000,
        },
      });
      return;
    }

    if (comments.length > 1000) {
      showToast({
        title: "Error",
        description: "Los comentarios no deben exceder los 1000 caracteres",
        options: {
          type: "error",
          duration: 4000,
        },
      });
      return;
    }

    exemptPayment(
      {
        payment_request_id: paymentRequestId,
        comments: comments.trim(),
        attachment,
      },
      {
        onSuccess: () => {
          showToast({
            title: "Éxito",
            description: "El pago ha sido eximido correctamente",
            options: {
              type: "success",
              duration: 4000,
            },
          });
          handleClose();
        },
        onError: (error) => {
          showToast({
            title: "Error",
            description:
              error instanceof Error
                ? error.message
                : "No se pudo eximir el pago. Por favor, intenta de nuevo.",
            options: {
              type: "error",
              duration: 4000,
            },
          });
        },
      },
    );
  };

  const handleClose = () => {
    setComments("");
    setAttachment(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        showToast({
          title: "Error",
          description: "El archivo no debe superar los 10MB",
          options: {
            type: "error",
            duration: 4000,
          },
        });
        return;
      }

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "image/jpeg",
        "image/png",
      ];
      if (!allowedTypes.includes(file.type)) {
        showToast({
          title: "Error",
          description:
            "Formato de archivo no permitido. Solo se permiten archivos PDF, DOC, DOCX, JPG y PNG",
          options: {
            type: "error",
            duration: 4000,
          },
        });
        return;
      }

      setAttachment(file);
    }
  };

  const handleRemoveFile = () => {
    setAttachment(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isPending) {
      setIsDragging(true);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (isPending) return;

    const file = e.dataTransfer.files?.[0];
    if (file) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        showToast({
          title: "Error",
          description: "El archivo no debe superar los 10MB",
          options: {
            type: "error",
            duration: 4000,
          },
        });
        return;
      }

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "image/jpeg",
        "image/png",
      ];
      if (!allowedTypes.includes(file.type)) {
        showToast({
          title: "Error",
          description:
            "Formato de archivo no permitido. Solo se permiten archivos PDF, DOC, DOCX, JPG y PNG",
          options: {
            type: "error",
            duration: 4000,
          },
        });
        return;
      }

      setAttachment(file);
    }
  };

  const isButtonDisabled = isPending || !comments.trim();

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Eximición de pago"
      description="¿Estás seguro que deseas eximir el pago? El solicitante no tendrá que realizar el pago y el trámite continuará."
      modalContentClassName="max-w-[520px] w-full gap-6"
    >
      <div className="flex flex-col gap-4">
        {/* Comments Field */}
        <div className="flex flex-col gap-2">
          <label
            htmlFor="comments"
            className="text-left text-sm font-semibold text-body-loud"
          >
            Razón de la eximición<span className="text-danger">*</span>
          </label>
          <Textarea
            id="comments"
            placeholder="Escribe tu comentario..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={4}
            maxLength={1000}
            disabled={isPending}
            className="resize-none"
          />
          <p className="text-xs text-body-quiet text-right">
            Caracteres: {comments.length}/1000
          </p>
        </div>

        {/* File Upload Section */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label className="text-left text-sm font-semibold text-body-loud">
              Documento justificante
            </label>
            <span className="text-xs text-body-quiet">Opcional</span>
          </div>
          <input
            ref={fileInputRef}
            id="attachment"
            type="file"
            onChange={handleFileChange}
            disabled={isPending}
            className="hidden"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          />
          <div className="flex flex-col gap-2">
            {!attachment ? (
              <div
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-6 flex flex-col items-center gap-2 transition-colors ${
                  isDragging
                    ? "border-action bg-action-silent"
                    : "border-neutral-quiet"
                }`}
              >
                <p className="text-sm text-body text-center">
                  Arrastra y Suelta o{" "}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isPending}
                    className="text-action underline hover:text-action-accent"
                  >
                    Selecciona un archivo
                  </button>
                </p>
                <p className="text-xs text-body-quiet text-center">
                  Sube archivos en formato .pdf
                </p>
              </div>
            ) : (
              <FileItem
                fileName={attachment.name}
                tag={`${(attachment.size / 1024).toFixed(0)} KB`}
                color="neutral"
                action={<Icon type="far" name="trash" size="sm" />}
                actionAriaLabel="Eliminar archivo"
                onAction={handleRemoveFile}
              />
            )}
          </div>
        </div>
      </div>

      <div className="w-full flex gap-3 pt-2">
        <Button
          type="button"
          size="lg"
          variant="secondary"
          className="flex-1"
          onClick={handleClose}
          disabled={isPending}
        >
          Cancelar
        </Button>
        <Button
          type="button"
          size="lg"
          variant={isButtonDisabled ? "quiet" : "warning-primary"}
          className="flex-1"
          onClick={handleSubmit}
          disabled={isButtonDisabled}
          isLoading={isPending}
        >
          Eximir pago
        </Button>
      </div>
    </Modal>
  );
};
